import { createSvgEl } from '@learnforge/demo-render';

export interface ClockOptions {
  positions: number;
  highlighted: ReadonlySet<number>;
  hand?: { angle: number; animateTo?: number; durationMs?: number };
  arcs?: { from: number; to: number; className: string }[];
  width: number;
  height: number;
}

export interface ClockHandle {
  group: SVGGElement;
  update(opts: Partial<ClockOptions>): void;
  destroy(): void;
}

export function createClock(parent: SVGGElement, initial: ClockOptions): ClockHandle {
  const group = createSvgEl('g', { class: 'arith-clock' });
  parent.appendChild(group);
  let opts = { ...initial };

  function render(): void {
    while (group.firstChild) group.removeChild(group.firstChild);
    const { positions, highlighted, hand, width, height } = opts;
    const cx = width / 2;
    const cy = height / 2;
    const r = Math.min(width, height) / 2 - 20;
    const face = createSvgEl('circle', {
      class: 'arith-clock-face',
      cx,
      cy,
      r,
    });
    group.appendChild(face);
    for (let i = 0; i < positions; i += 1) {
      const angle = (i / positions) * Math.PI * 2 - Math.PI / 2;
      const tx = cx + r * Math.cos(angle);
      const ty = cy + r * Math.sin(angle);
      const cls = highlighted.has(i) ? 'arith-clock-tick glow' : 'arith-clock-tick';
      const tick = createSvgEl('circle', {
        class: cls,
        cx: tx,
        cy: ty,
        r: 6,
        'data-position': i,
      });
      group.appendChild(tick);
      const label = createSvgEl('text', {
        class: 'arith-clock-label',
        x: tx,
        y: ty - 14,
        'text-anchor': 'middle',
      });
      label.textContent = String(i);
      group.appendChild(label);
    }
    if (hand) {
      const handEnd = {
        x: cx + r * 0.85 * Math.cos(hand.angle - Math.PI / 2),
        y: cy + r * 0.85 * Math.sin(hand.angle - Math.PI / 2),
      };
      const line = createSvgEl('line', {
        class: 'arith-clock-hand',
        x1: cx,
        y1: cy,
        x2: handEnd.x,
        y2: handEnd.y,
      });
      group.appendChild(line);
    }
  }

  render();

  return {
    group,
    update(next: Partial<ClockOptions>) {
      opts = { ...opts, ...next };
      render();
    },
    destroy() {
      if (group.parentNode) group.parentNode.removeChild(group);
    },
  };
}
